'use client'

import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Trash2, LineChart } from 'lucide-react'
import JSZip from 'jszip';
import type {
  StoryboardProject,
  StoryboardProjectDraft,
  StoryboardProjectImportPayload,
  StoryboardSegment,
} from '../types';
import {
  saveVersionSnapshot,
  listMediaRecordsByProject,
  type StoryboardMediaImportAsset,
} from '../storage';
import type { StoryboardMediaRecord } from '../types';
import { useStoryboard } from '../context';
import { ensureMediaRecordForPreview, guessMediaFileExtension } from '../utils/mediaCache';
import { createPreviewEntry, resolvePreviewEntryDisplayUrl } from '../utils/mediaHelpers';
import { guessSoraVideoIdFromSegment, isSoraModelId } from '../utils/soraHelpers';
import { triggerBlobDownload } from '@/client/utils/fileSaver';

const ProjectDialog: React.FC<{
  open: boolean;
  loading: boolean;
  existingNames: string[];
  onClose: () => void;
  onSubmit: (draft: StoryboardProjectDraft) => Promise<boolean>;
}> = ({ open, loading, existingNames, onClose, onSubmit }) => {
  const [name, setName] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    if (loading) return;
    setName('');
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    const nextErrors: Record<string, string> = {};
    const trimmedName = name.trim();
    if (!trimmedName) {
      nextErrors.name = '请输入项目名称';
    }
    if (existingNames.includes(trimmedName)) {
      nextErrors.name = '项目名称已存在';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    const success = await onSubmit({
      name: trimmedName,
    });
    if (success) {
      setName('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>新建故事板项目</DialogTitle>
        </DialogHeader>
        <div className="mt-1 space-y-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">项目名称</label>
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                if (errors.name) setErrors({})
              }}
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name ? (
              <div className="text-xs text-destructive mt-1">{errors.name}</div>
            ) : null}
          </div>
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? '保存中...' : '保存项目'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
};

const ProjectSelectionDialog: React.FC<{
  open: boolean;
  loading: boolean;
  projects: StoryboardProject[];
  activeProjectId: string | null;
  onClose: () => void;
  onSelect: (projectId: string) => void;
  onDelete: (projectId: string) => void;
  disabled?: boolean;
}> = ({ open, loading, projects, activeProjectId, onClose, onSelect, onDelete, disabled }) => (
  <Dialog open={open} onOpenChange={(v) => (!disabled && !v ? onClose() : undefined)}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>打开故事板项目</DialogTitle>
      </DialogHeader>
      {loading ? (
        <div className="flex items-center justify-center gap-2 text-muted-foreground py-2">
          <Loader2 className="size-4 animate-spin" /> <span className="text-sm">加载项目中…</span>
        </div>
      ) : projects.length ? (
        <div className="space-y-1">
          {projects.map((project) => {
            const selected = project.projectId === activeProjectId
            return (
              <button
                key={project.projectId}
                disabled={disabled}
                onClick={() => onSelect(project.projectId)}
                className={[
                  'w-full flex items-center justify-between rounded-md border p-2 text-left',
                  selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                ].join(' ')}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <LineChart className={['size-4', selected ? 'text-primary' : 'opacity-70'].join(' ')} />
                  <div className="text-sm font-medium truncate">{project.name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[11px] text-muted-foreground">
                    最近更新：{project.lastUpdated ? new Date(project.lastUpdated).toLocaleString() : '暂无'}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center size-7 rounded-sm border hover:bg-muted/60"
                        onClick={(event) => {
                          event.stopPropagation()
                          onDelete(project.projectId)
                        }}
                        disabled={disabled}
                        aria-label="删除项目"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>删除项目</TooltipContent>
                  </Tooltip>
                </div>
              </button>
            )
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-2">
          还没有故事板项目，点击“新建项目”按钮开始创建。
        </div>
      )}
      <DialogFooter>
        <Button onClick={onClose} disabled={disabled} variant="outline">
          关闭
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

type FeedbackState = {
  message: string
  severity: 'success' | 'info' | 'warning' | 'error'
}

const menuButtonClass =
  'text-[11px] text-foreground px-2.5 rounded-lg leading-5 min-h-8 whitespace-nowrap hover:bg-muted'

const sanitizeFileName = (value: string): string =>
  value
    .trim()
    .replace(/[\p{Cc}<>:"/\\|?*]+/gu, '_')
    .replace(/\s+/g, '_');

const resolveMediaPath = (
  record: StoryboardMediaRecord,
  usedPaths: Set<string>
): { path: string; fileName: string } => {
  const defaultExt = guessMediaFileExtension(record.mimeType);
  const rawName = record.fileName?.split('/').pop() ?? `${record.previewId}.${defaultExt}`;
  const sanitized = sanitizeFileName(rawName || `${record.previewId}.${defaultExt}`);
  const dotIndex = sanitized.lastIndexOf('.');
  const namePart = dotIndex > 0 ? sanitized.slice(0, dotIndex) : sanitized;
  const extPart =
    dotIndex > 0 ? sanitized.slice(dotIndex) : defaultExt ? `.${defaultExt}` : '';
  let candidate = sanitized || `${record.previewId}.${defaultExt}`;
  let path = `media/${candidate}`;
  let counter = 1;
  while (usedPaths.has(path)) {
    const suffix = `_${counter}`;
    const nextName = `${namePart || record.previewId}${suffix}${extPart}`;
    candidate = sanitizeFileName(nextName);
    path = `media/${candidate}`;
    counter += 1;
  }
  usedPaths.add(path);
  return { path, fileName: candidate };
};

const isZipFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return name.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
};

export const ProjectMenuBar: React.FC = () => {
  const {
    projects,
    segments,
    loadingProjects,
    mutating,
    activeProjectId,
    refreshProjects,
    selectProject,
    createProject,
    deleteProject,
    importProject,
    updateSegment,
  } = useStoryboard();

  const [projectDialogOpen, setProjectDialogOpen] = useState<boolean>(false);
  const [projectListOpen, setProjectListOpen] = useState<boolean>(false);
  const [pendingDeleteProjectId, setPendingDeleteProjectId] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const activeProject = useMemo(
    () => projects.find((project) => project.projectId === activeProjectId) ?? null,
    [projects, activeProjectId]
  );

  const handleCreateProject = async (draft: StoryboardProjectDraft) => {
    const project = await createProject(draft);
    return Boolean(project);
  };

  const handleDeleteProject = async (projectId: string) => {
    await deleteProject(projectId);
    setPendingDeleteProjectId(null);
  };

  const showFeedback = (message: string, severity: FeedbackState['severity'] = 'success') => {
    setFeedback({ message, severity })
  }

  const handleFeedbackClose = () => setFeedback(null);

  const handleOpenProjectDialog = () => {
    void refreshProjects();
    setProjectListOpen(true);
  };

  const handleSaveProject = async () => {
    if (!activeProjectId || !activeProject) {
      showFeedback('请先选择或创建一个项目', 'warning');
      return;
    }
    setSaving(true);
    try {
      const versionId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      await saveVersionSnapshot({
        versionId,
        projectId: activeProjectId,
        label: `手动保存 ${new Date().toLocaleString()}`,
        createdAt: Date.now(),
        segments: segments.map((segment) => ({ ...segment })),
      });
      showFeedback('已保存当前项目快照');
      await refreshProjects();
    } catch (error) {
      console.error('[storyboard] 保存项目失败', error);
      showFeedback('保存失败，请稍后重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadProject = async () => {
    if (!activeProjectId || !activeProject) {
      showFeedback('请先选择或创建一个项目', 'warning');
      return;
    }
    if (exporting) {
      return;
    }
    setExporting(true);
    try {
      const segmentsSnapshot = [...segments];
      const preparedSegments: StoryboardSegment[] = [];

      for (const segment of segmentsSnapshot) {
        let nextSegment: StoryboardSegment = segment;
        let nextEntries: StoryboardSegment['previewEntries'] = segment.previewEntries;
        let nextSelectedId: string | undefined = segment.selectedPreviewId ?? undefined;
        let nextPreviewUrl = segment.previewUrl;
        let mutated = false;

        if (isSoraModelId(segment.modelId)) {
          const videoId = guessSoraVideoIdFromSegment(segment);
          if (segment.previewEntries.length === 0 && segment.status === 'success' && videoId) {
            const newEntry = createPreviewEntry(segment.segmentId, undefined, {
              taskId: segment.taskId,
              downloadSource: { type: 'sora', videoId },
            });
            nextEntries = [newEntry];
            nextSelectedId = newEntry.previewId;
            nextPreviewUrl = resolvePreviewEntryDisplayUrl(newEntry);
            mutated = true;
          } else if (videoId) {
            const patchedEntries: StoryboardSegment['previewEntries'] = [];
            let entriesChanged = false;
            for (const entry of segment.previewEntries) {
              if (entry.url) {
                patchedEntries.push(entry);
                continue;
              }
              if (entry.downloadSource?.type === 'sora' && entry.downloadSource.videoId === videoId) {
                patchedEntries.push(entry);
                continue;
              }
              entriesChanged = true;
              patchedEntries.push({
                ...entry,
                downloadSource: { type: 'sora', videoId },
              });
            }
            if (entriesChanged) {
              nextEntries = patchedEntries;
              mutated = true;
            }
            if ((!nextPreviewUrl || !nextPreviewUrl.trim()) && (nextEntries.length > 0 || segment.previewEntries.length > 0)) {
              const lookupEntries = nextEntries.length ? nextEntries : segment.previewEntries;
              const preferredEntry =
                lookupEntries.find((entry) => entry.previewId === (nextSelectedId ?? segment.selectedPreviewId ?? undefined)) ??
                lookupEntries[0];
              if (preferredEntry) {
                const resolvedUrl = resolvePreviewEntryDisplayUrl(preferredEntry);
                if (resolvedUrl && resolvedUrl !== nextPreviewUrl) {
                  nextPreviewUrl = resolvedUrl;
                  mutated = true;
                }
                if (!nextSelectedId) {
                  nextSelectedId = preferredEntry.previewId;
                  mutated = true;
                }
              }
            }
          }
        }

        if (mutated) {
          const updatePayload = {
            previewEntries: nextEntries,
            selectedPreviewId: nextSelectedId,
            previewUrl: nextPreviewUrl,
          };
          const updated = await updateSegment(segment.segmentId, updatePayload);
          nextSegment = updated ?? { ...segment, ...updatePayload };
        }

        preparedSegments.push(nextSegment);
      }

      const existingRecords = await listMediaRecordsByProject(activeProjectId);
      const recordMap = new Map(existingRecords.map((record) => [record.previewId, record]));
      const collectedRecords: StoryboardMediaRecord[] = [];
      const missingPreviewIds = new Set<string>();
      const seenRecords = new Set<string>();

      for (const segment of preparedSegments) {
        for (const entry of segment.previewEntries) {
          if (!entry?.previewId) continue;
          let record = recordMap.get(entry.previewId);
          if (!record) {
            try {
              const ensuredRecord = await ensureMediaRecordForPreview({
                projectId: segment.projectId,
                segmentId: segment.segmentId,
                entry,
              });
              if (ensuredRecord) {
                record = ensuredRecord;
                recordMap.set(entry.previewId, ensuredRecord);
              }
            } catch (error) {
              console.warn('[storyboard] 缓存视频失败', error);
            }
          }
          if (record) {
            if (!seenRecords.has(record.previewId)) {
              seenRecords.add(record.previewId);
              collectedRecords.push(record);
            }
          } else {
            missingPreviewIds.add(entry.previewId);
          }
        }
      }

      const zip = new JSZip();
      const usedPaths = new Set<string>();
      const mediaMetadata = collectedRecords.map((record) => {
        const { path } = resolveMediaPath(record, usedPaths);
        zip.file(path, record.blob);
        return {
          previewId: record.previewId,
          segmentId: record.segmentId,
          fileName: path,
          mimeType: record.mimeType,
          size: record.size,
          originalUrl: record.originalUrl,
        };
      });

      const payload = {
        exportedAt: new Date().toISOString(),
        version: 2,
        project: activeProject,
        segments: preparedSegments.map((segment) => ({ ...segment })),
        media: mediaMetadata,
      };

      zip.file('project.json', JSON.stringify(payload, null, 2));

      const archive = await zip.generateAsync({ type: 'blob' });
      const prettyName = activeProject.name
        ? activeProject.name.trim().replace(/[^a-zA-Z0-9\u4e00-\u9fa5-_]+/g, '_')
        : 'storyboard_project';
      await triggerBlobDownload(archive, `${prettyName || 'storyboard_project'}.zip`);

      if (missingPreviewIds.size > 0) {
        showFeedback(
          `已打包项目，包含 ${mediaMetadata.length} 个视频，另有 ${missingPreviewIds.size} 个视频仍需联网加载`,
          'warning'
        );
      } else {
        showFeedback(`已打包项目并包含 ${mediaMetadata.length} 个视频`);
      }
    } catch (error) {
      console.error('[storyboard] 下载项目失败', error);
      showFeedback('下载失败，请稍后重试', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportButtonClick = () => {
  if (mutating || importing) {
    return;
  }
  fileInputRef.current?.click();
};

  const parseStoryboardArchive = async (
    file: File
  ): Promise<{
    payload: StoryboardProjectImportPayload;
    assets: StoryboardMediaImportAsset[];
    missing: string[];
  }> => {
    const buffer = await file.arrayBuffer();
    let zip: JSZip;
    try {
      zip = await JSZip.loadAsync(buffer);
    } catch (error) {
      console.error('[storyboard] 解压项目归档失败', error);
      throw new Error('压缩包解析失败，请确认文件格式正确');
    }
    const jsonFile =
      zip.file('project.json') ??
      zip.file('Project.json') ??
      zip
        .filter((relativePath) => relativePath.toLowerCase().endsWith('.json'))
        .find((entry) => !entry.name.toLowerCase().startsWith('media/'));
    if (!jsonFile) {
      throw new Error('压缩包内缺少项目描述文件 project.json');
    }
    let payload: StoryboardProjectImportPayload;
    try {
      const text = await jsonFile.async('string');
      payload = JSON.parse(text) as StoryboardProjectImportPayload;
    } catch (error) {
      console.error('[storyboard] 解析项目描述文件失败', error);
      throw new Error('项目描述文件解析失败，请确认文件内容是否完整');
    }

    const descriptors = Array.isArray(payload.media) ? payload.media : [];
    const assets: StoryboardMediaImportAsset[] = [];
    const missing: string[] = [];

    for (const descriptor of descriptors) {
      if (!descriptor?.previewId) continue;
      const searchPaths = [
        descriptor.fileName,
        descriptor.fileName ? descriptor.fileName.replace(/^\.?\/+/, '') : null,
        `media/${descriptor.previewId}`,
        `media/${descriptor.previewId}.${guessMediaFileExtension(descriptor.mimeType)}`,
        descriptor.previewId,
      ].filter((item): item is string => Boolean(item));
      let entry: JSZip.JSZipObject | null = null;
      for (const candidate of searchPaths) {
        entry = zip.file(candidate);
        if (entry) break;
      }
      if (!entry) {
        missing.push(descriptor.previewId);
        continue;
      }
      const arrayBuffer = await entry.async('arraybuffer');
      const mimeType = descriptor.mimeType ?? 'application/octet-stream';
      const blob = new Blob([arrayBuffer], { type: mimeType });
      assets.push({
        sourcePreviewId: descriptor.previewId,
        blob,
        mimeType,
        fileName: descriptor.fileName ?? entry.name,
        originalUrl: descriptor.originalUrl,
      });
    }

    return { payload, assets, missing };
  };

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setImporting(true);
    try {
      let payload: StoryboardProjectImportPayload;
      let mediaAssets: StoryboardMediaImportAsset[] = [];
      let missingAssets: string[] = [];

      if (isZipFile(file)) {
        const parsed = await parseStoryboardArchive(file);
        payload = parsed.payload;
        mediaAssets = parsed.assets;
        missingAssets = parsed.missing;
      } else {
        const text = await file.text();
        try {
          payload = JSON.parse(text) as StoryboardProjectImportPayload;
        } catch {
          throw new Error('文件解析失败，请确保选择 JSON 或导出的故事板归档');
        }
      }

      const project = await importProject(payload, mediaAssets);
      if (project) {
        if (missingAssets.length) {
          showFeedback(
            `已导入项目「${project.name}」，但有 ${missingAssets.length} 个视频文件缺失`,
            'warning'
          );
        } else if (mediaAssets.length) {
          showFeedback(`已导入项目「${project.name}」，包含 ${mediaAssets.length} 个视频资源`);
        } else {
          showFeedback(`已导入项目「${project.name}」`);
        }
      } else {
        showFeedback('导入失败，请检查文件内容是否正确', 'error');
      }
    } catch (error) {
      console.error('[storyboard] 导入项目失败', error);
      const message =
        error instanceof Error ? `导入失败：${error.message}` : '导入失败，请稍后重试';
      showFeedback(message, 'error');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <>
      <Button variant="ghost" size="xs" className={menuButtonClass} onClick={handleOpenProjectDialog} disabled={loadingProjects || mutating}>
        打开已有项目
      </Button>
      <Button variant="ghost" size="xs" className={menuButtonClass} onClick={() => setProjectDialogOpen(true)} disabled={mutating}>
        新建项目
      </Button>
      <Button variant="ghost" size="xs" className={menuButtonClass} onClick={handleSaveProject} disabled={saving || mutating}>
        {saving ? '保存中…' : '保存'}
      </Button>
      <Button variant="ghost" size="xs" className={menuButtonClass} onClick={handleDownloadProject} disabled={mutating || exporting}>
        {exporting ? '打包中…' : '下载当前项目'}
      </Button>
      <Button variant="ghost" size="xs" className={menuButtonClass} onClick={handleImportButtonClick} disabled={mutating || importing}>
        {importing ? '导入中…' : '导入项目'}
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,application/zip,.json,.zip"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />

      <Dialog open={Boolean(pendingDeleteProjectId)} onOpenChange={(v) => !v && setPendingDeleteProjectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除项目</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            项目删除后本地故事板数据将被清空且不可恢复，确认继续删除？
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteProjectId(null)} disabled={mutating}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={() => pendingDeleteProjectId && handleDeleteProject(pendingDeleteProjectId)}
              disabled={mutating}
            >
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProjectDialog
        open={projectDialogOpen}
        loading={mutating}
        existingNames={projects.map((project) => project.name.trim())}
        onClose={() => setProjectDialogOpen(false)}
        onSubmit={handleCreateProject}
      />

      <ProjectSelectionDialog
        open={projectListOpen}
        loading={loadingProjects}
        projects={projects}
        activeProjectId={activeProjectId}
        onClose={() => setProjectListOpen(false)}
        onSelect={(projectId) => {
          selectProject(projectId)
          setProjectListOpen(false)
        }}
        onDelete={(projectId) => setPendingDeleteProjectId(projectId)}
        disabled={mutating}
      />

      {feedback ? (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(480px,calc(100%-2rem))]">
          <Alert className="shadow-md" variant={feedback.severity === 'error' ? 'destructive' : 'default'}>
            <AlertDescription className="text-sm flex items-center justify-between">
              <span>{feedback.message}</span>
              <Button variant="outline" size="sm" onClick={handleFeedbackClose}>
                关闭
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : null}
    </>
  )
};

export default ProjectMenuBar;
